import './CopTable.scss'

import Tooltip from '@mui/material/Tooltip'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

const DateTimeTooltip = ({ displayValue, tooltipValue }: { displayValue: string; tooltipValue: string }) => (
  <Tooltip title={tooltipValue} placement="top" arrow classes={{ tooltip: 'cop-datetime-tooltip', arrow: 'cop-datetime-tooltip__arrow' }}>
    <span className="cop-datetime-cell">{displayValue}</span>
  </Tooltip>
)

export type CaseColumnTitles = {
  caseNumber?: string
  subject?: string
  customerReference?: string
  status?: string
  dateTimeOpened?: string
  product?: string
  view?: string
  severity?: string
}

const getDetailUrl = (id: string, viewPageUrl?: string) => {
  if (viewPageUrl) return `${viewPageUrl}/${id}`
  const basePath = window.location.pathname.replace(/\/[^/]*$/, '')
  return `${window.location.origin}${basePath}/case-details/${id}`
}

const CaseLink = ({ value, id, viewPageUrl }: { value: string; id: string; viewPageUrl?: string }) => (
  <a href={getDetailUrl(id, viewPageUrl)} className="cop-case-link">
    {value}
  </a>
)

const StatusCell = ({ value }: { value?: string | null }) => {
  if (!value) return <>-</>;
  return (
    <span className="cop-status-cell">
      <span className={`cop-status-dot cop-status-dot--${value.toLowerCase()}`} />
      <span>{value}</span>
    </span>
  )
}

const CaseStatusCell = ({ value }: { value: string }) => {
  const isMyAction = value?.trim().toLowerCase() === 'my action'
  return (
    <span className="cop-status-cell">
      {isMyAction && <span className="cop-status-dot cop-status-dot--my-action" />}
      <span>{value}</span>
    </span>
  )
}

const ViewButton = ({ id, viewPageUrl, viewLabel = 'View' }: { id: string; viewPageUrl?: string; viewLabel?: string }) => (
  <button
    className="cop-view-button"
    onClick={() => {
      window.location.href = getDetailUrl(id, viewPageUrl)
    }}
  >
    {viewLabel}
  </button>
)

export function getCaseColumns(columnTitles: CaseColumnTitles = {}, viewPageUrl?: string, canViewCaseDetails?: boolean): GridColDef[] {
  return [
    {
      field: 'number',
      headerName: columnTitles.caseNumber ?? 'Case Number',
      flex: 1,
      minWidth: 180,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => <CaseLink value={params.value} id={String(params.row.id)} viewPageUrl={viewPageUrl} />,
    },
    {
      field: 'subject',
      headerName: columnTitles.subject ?? 'Subject',
      flex: 0.8,
      minWidth: 130,
      filterable: false,
    },
    {
      field: 'customerReference',
      headerName: columnTitles.customerReference ?? 'Customer Reference',
      flex: 0.8,
      minWidth: 130,
      filterable: false,
    },
    {
      field: 'status',
      headerName: columnTitles.status ?? 'Status',
      flex: 0.7,
      minWidth: 120,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => <CaseStatusCell value={params.value} />,
    },
    {
      field: 'dateTimeOpened',
      headerName: columnTitles.dateTimeOpened ?? 'Date/Time Opened',
      flex: 0.8,
      minWidth: 130,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return '—'
        const date = new Date(params.value)
        const displayValue = date
          .toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })
          .replace(',', '')
        const tooltipValue =
          date
            .toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
              timeZone: 'UTC',
            })
            .replace(/(\d{4}),\s*/, '$1 ') + ', London time, UTC'
        return <DateTimeTooltip displayValue={displayValue} tooltipValue={tooltipValue} />
      },
    },
    {
      field: 'product',
      headerName: columnTitles.product ?? 'Product',
      flex: 1,
      minWidth: 180,
      filterable: true,
    },
    {
      field: 'severity',
      headerName: columnTitles.severity ?? 'Severity',
      flex: 1,
      minWidth: 180,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => <StatusCell value={params.value} />,
    },
    ...(canViewCaseDetails
      ? [
          {
            field: 'actions',
            headerName: columnTitles.view ?? 'View',
            flex: 0.5,
            minWidth: 130,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => (
              <ViewButton id={String(params.row.id)} viewPageUrl={viewPageUrl} viewLabel={columnTitles.view} />
            ),
          },
        ]
      : []),
  ]
}
